import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';
import API_BASE from '../../utils/api';

const UNIFORM_TYPES = {
  shirt: '👔 Shirt', pant: '👖 Pant', skirt: '👗 Skirt', tie: '🎀 Tie', belt: 'Belt',
  socks: '🧦 Socks', shoes: '👟 Shoes', blazer: '🧥 Blazer', sweater: '🧶 Sweater',
  pe_shirt: '🏃 PE Shirt', pe_pant: '🏃 PE Pant', other: 'Other'
};

const ParentMaterials = () => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMaterials = async () => {
      try {
        const res = await axios.get(`${API_BASE}/materials/my-child`);
        setMaterials(res.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchMaterials();
  }, []);

  const totalBooksCost = (books) => books.reduce((sum, b) => sum + (Number(b.price) || 0), 0);

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 lg:ml-64">
        <header className="bg-white shadow-sm border-b px-6 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">📚 Books & Uniform</h1>
            <p className="text-sm text-gray-500">Required books, dress & shoes for your child</p>
          </div>
          <ProfileDropdown />
        </header>

        <main className="p-6">
          {loading ? <p>Loading...</p> : materials.length === 0 ? (
            <div className="bg-white rounded-xl shadow p-10 text-center">
              <div className="text-5xl mb-4">📚</div>
              <h3 className="text-lg font-semibold text-gray-700">No Materials Listed</h3>
              <p className="text-gray-500">Materials haven't been assigned for your child's class yet.</p>
            </div>
          ) : (
            materials.map(mat => (
              <div key={mat._id} className="bg-white rounded-xl shadow-sm border overflow-hidden mb-6">
                <div className="px-6 py-4 bg-gradient-to-r from-indigo-500 to-blue-500">
                  <h3 className="font-bold text-lg text-white">{mat.classId?.className} - {mat.classId?.section}</h3>
                  <p className="text-sm text-blue-100">Academic Year: {mat.academicYear}</p>
                </div>

                <div className="p-6">
                  {/* Books */}
                  <div className="mb-6">
                    <h4 className="font-bold text-gray-800 mb-3 text-lg">📖 Required Books</h4>
                    {mat.books.length === 0 ? <p className="text-gray-400 text-sm">No books assigned</p> : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm border rounded-lg overflow-hidden">
                          <thead className="bg-indigo-50">
                            <tr>
                              <th className="px-4 py-3 text-left">#</th>
                              <th className="px-4 py-3 text-left">Book Name</th>
                              <th className="px-4 py-3 text-left">Subject</th>
                              <th className="px-4 py-3 text-left">Author</th>
                              <th className="px-4 py-3 text-left">Publisher</th>
                              <th className="px-4 py-3 text-right">Price</th>
                              <th className="px-4 py-3 text-center">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {mat.books.map((b, i) => (
                              <tr key={i} className={`border-t ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                                <td className="px-4 py-3">{i + 1}</td>
                                <td className="px-4 py-3 font-semibold text-gray-800">{b.name}</td>
                                <td className="px-4 py-3 text-gray-600">{b.subject}</td>
                                <td className="px-4 py-3 text-gray-600">{b.author || '-'}</td>
                                <td className="px-4 py-3 text-gray-600">{b.publisher || '-'}</td>
                                <td className="px-4 py-3 text-right font-semibold text-green-700">₹{b.price || 0}</td>
                                <td className="px-4 py-3 text-center">
                                  {b.isRequired ? (
                                    <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full font-medium">Required</span>
                                  ) : (
                                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">Optional</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-indigo-50">
                            <tr>
                              <td colSpan="5" className="px-4 py-3 font-bold text-right">Total Books Cost:</td>
                              <td className="px-4 py-3 text-right font-bold text-green-800">₹{totalBooksCost(mat.books)}</td>
                              <td></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Uniform & Shoes */}
                  <div className="mb-4">
                    <h4 className="font-bold text-gray-800 mb-3 text-lg">👔 Uniform & Shoes</h4>
                    {mat.uniformItems.length === 0 ? <p className="text-gray-400 text-sm">No uniform items assigned</p> : (
                      <div className="grid md:grid-cols-2 gap-4">
                        {mat.uniformItems.map((u, i) => (
                          <div key={i} className="border rounded-xl p-4 hover:shadow-sm transition bg-gray-50">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <h5 className="font-semibold text-gray-800">{UNIFORM_TYPES[u.type] || u.type} — {u.itemName}</h5>
                                <p className="text-xs text-gray-500">
                                  {u.gender === 'all' ? 'All Students' : u.gender === 'boys' ? '👦 Boys Only' : '👧 Girls Only'}
                                  {u.color && ` • Color: ${u.color}`}
                                </p>
                              </div>
                              {u.isRequired ? (
                                <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full font-medium">Required</span>
                              ) : (
                                <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded-full">Optional</span>
                              )}
                            </div>
                            <div className="mt-2">
                              <p className="text-xs font-medium text-gray-600 mb-1">Available Sizes:</p>
                              <div className="flex flex-wrap gap-2">
                                {u.availableSizes.map((s, si) => (
                                  <span key={si} className="text-sm bg-white border-2 border-indigo-200 rounded-lg px-3 py-1 font-medium">
                                    {s.size} <span className="text-green-600">₹{s.price}</span>
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {mat.notes && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                      <p className="text-sm text-yellow-800">📝 <strong>Note:</strong> {mat.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </main>
      </div>
    </div>
  );
};

export default ParentMaterials;
